import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const stats = [
  { value: '55+', label: 'Years of Excellence' },
  { value: '3', label: 'ISO Certifications' },
  { value: '5+', label: 'Countries Supplied' },
  { value: 'Tier-1', label: 'OEM Supplier' },
];

const capabilities = [
  {
    title: 'In-House Casting',
    description: 'Sand Casting and Gravity Die Casting for copper alloys including Gun Metal, Leaded Bronze, Aluminium Bronze, Phosphorous Bronze, and High Tensile Brass.',
    link: '/infrastructure',
    linkLabel: 'See Casting Infrastructure',
    color: 'amber',
    icon: (
      <svg className="w-7 h-7 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
      </svg>
    ),
  },
  {
    title: 'CNC & VMC Machining',
    description: 'Mazak VMCs and CNC Lathes (Doosan, Tsugami, LMW) delivering tight tolerances. Every workstation integrated with SPC software for real-time quality monitoring.',
    link: '/infrastructure',
    linkLabel: 'See Machining Infrastructure',
    color: 'blue',
    icon: (
      <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    title: 'Precision Inspection',
    description: 'Mitutoyo CMM & Contracer, Hitachi Spectrometer, and SPC Edge software on every workstation. Cp/Cpk monitoring for all critical dimensions. Data retained 3 years.',
    link: '/infrastructure',
    linkLabel: 'See Quality Lab',
    color: 'green',
    icon: (
      <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>
    ),
  },
];

const capabilityBg = {
  amber: 'bg-amber-50 border-amber-100 hover:border-amber-300 hover:bg-amber-50',
  blue:  'bg-blue-50  border-blue-100  hover:border-blue-300  hover:bg-blue-50',
  green: 'bg-green-50 border-green-100 hover:border-green-300 hover:bg-green-50',
};
const capabilityIconBg = {
  amber: 'bg-amber-100 group-hover:bg-amber-200',
  blue:  'bg-blue-100  group-hover:bg-blue-200',
  green: 'bg-green-100 group-hover:bg-green-200',
};
const capabilityLink = {
  amber: 'text-amber-700 group-hover:text-amber-800',
  blue:  'text-blue-600  group-hover:text-blue-700',
  green: 'text-green-600 group-hover:text-green-700',
};

const products = [
  { id: 1,  name: 'Aluminium Bronze Flange', image: '/images/products/product_13.webp', alt: 'Aluminium bronze flange bushing for automotive OEM applications - Mato Industries Delhi' },
  { id: 2,  name: 'Brass Nozzle',            image: '/images/products/product_14.webp', alt: 'Precision brass pneumatic nozzle for hydraulic and pneumatic systems - Mato Industries Delhi' },
  { id: 3,  name: 'Bronze Collar Bush',      image: '/images/products/product_15.webp', alt: 'Phosphorous bronze collar bush for heavy-duty transportation drivetrain - Mato Industries Delhi' },
  { id: 4,  name: 'Split Bush',              image: '/images/products/product_16.webp', alt: 'High tensile brass split bush for industrial machinery tight tolerance applications - Mato Industries Delhi' },
  { id: 5,  name: 'Bronze Piston',           image: '/images/products/product_17.webp', alt: 'CNC machined bronze piston for automotive and transportation OEM assemblies - Mato Industries Delhi' },
  { id: 6,  name: 'Bronze Thrust Washer',    image: '/images/products/product_18.webp', alt: 'Bronze thrust washer for heavy equipment axle and gearbox assemblies - Mato Industries Delhi' },
];

const industries = [
  'Tractors & Agriculture',
  'Earth Movers',
  'Cranes & Lifting',
  'Heavy Machinery',
  'Refrigeration',
  'Railways',
  'Pump Industry',
  'Telecom',
];

const HomePage = () => (
  <>
    <Helmet>
      <title>Mato Industries – Bronze Bushing & Precision Casting Manufacturer, Delhi</title>
      <meta name="description" content="ISO-certified manufacturer of bronze bushings, thrust washers & copper alloy components. CNC/VMC machining. Est. 1968. Delhi, India." />
      <link rel="canonical" href="https://www.matoindustries.com/" />
      <meta property="og:url" content="https://www.matoindustries.com/" />
      <meta property="og:title" content="Mato Industries – Bronze Bushing & Precision Casting Manufacturer, Delhi" />
      <meta property="og:description" content="ISO-certified manufacturer of bronze bushings, thrust washers & copper alloy components. CNC/VMC machining. Est. 1968. Delhi, India." />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "Bronze Bushings & Copper Alloy Products by Mato Industries",
        "url": "https://www.matoindustries.com/products",
        "numberOfItems": 18,
        "itemListElement": [
          { "@type": "ListItem", "position": 1,  "name": "Aluminium Bronze Flange",          "url": "https://www.matoindustries.com/products" },
          { "@type": "ListItem", "position": 2,  "name": "Bronze Collar Bush",               "url": "https://www.matoindustries.com/products" },
          { "@type": "ListItem", "position": 3,  "name": "High Tensile Brass Split Bush",    "url": "https://www.matoindustries.com/products" },
          { "@type": "ListItem", "position": 4,  "name": "Bronze Piston",                    "url": "https://www.matoindustries.com/products" },
          { "@type": "ListItem", "position": 5,  "name": "Bronze Thrust Washer",             "url": "https://www.matoindustries.com/products" },
          { "@type": "ListItem", "position": 6,  "name": "Brass Pneumatic Nozzle",           "url": "https://www.matoindustries.com/products" },
          { "@type": "ListItem", "position": 7,  "name": "Threaded Bush",                    "url": "https://www.matoindustries.com/products" },
          { "@type": "ListItem", "position": 8,  "name": "Dielectric for Microwave Antenna", "url": "https://www.matoindustries.com/products" },
          { "@type": "ListItem", "position": 9,  "name": "Telecom Parts in Aluminium",       "url": "https://www.matoindustries.com/products" },
          { "@type": "ListItem", "position": 10, "name": "Feed for Microwave Antenna",       "url": "https://www.matoindustries.com/products" },
          { "@type": "ListItem", "position": 11, "name": "Bronze Shifter",                   "url": "https://www.matoindustries.com/products" },
          { "@type": "ListItem", "position": 12, "name": "Brass Pneumatic Parts",            "url": "https://www.matoindustries.com/products" },
          { "@type": "ListItem", "position": 13, "name": "Tractor Bush",                     "url": "https://www.matoindustries.com/products" },
          { "@type": "ListItem", "position": 14, "name": "Bronze Threaded Bush",             "url": "https://www.matoindustries.com/products" },
          { "@type": "ListItem", "position": 15, "name": "Tractor Bush (variant)",           "url": "https://www.matoindustries.com/products" },
          { "@type": "ListItem", "position": 16, "name": "Hydraulic Pump Plate",             "url": "https://www.matoindustries.com/products" },
          { "@type": "ListItem", "position": 17, "name": "Bronze Piston (heavy)",            "url": "https://www.matoindustries.com/products" },
          { "@type": "ListItem", "position": 18, "name": "Bush for Compressor",              "url": "https://www.matoindustries.com/products" }
        ]
      })}</script>
    </Helmet>
    <div className="flex flex-col min-h-screen bg-slate-50">

    {/* ── Hero ─────────────────────────────────────────── */}
    <section className="relative bg-hero-pattern text-white overflow-hidden">

      {/* Layer 1: Animated drifting grid */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          animation: 'heroGridDrift 6s linear infinite',
        }}
      />


      {/* Layer 3: Engineering crosshair markers */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        {[
          { right: '13%', top: '18%', delay: '0s',   size: 30 },
          { right: '28%', top: '72%', delay: '2.5s', size: 22 },
          { right: '5%',  top: '52%', delay: '4.5s', size: 26 },
          { right: '42%', top: '85%', delay: '1.2s', size: 18 },
          { right: '60%', top: '12%', delay: '3.8s', size: 16 },
        ].map((m, i) => (
          <div
            key={i}
            style={{
              position: 'absolute', right: m.right, top: m.top,
              animation: `heroTargetPulse 4s ease-in-out infinite`,
              animationDelay: m.delay,
            }}
          >
            <svg width={m.size} height={m.size} viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="5" stroke="rgba(251,191,36,0.9)" strokeWidth="1.2" />
              <line x1="16" y1="0"  x2="16" y2="10" stroke="rgba(251,191,36,0.8)" strokeWidth="0.8" />
              <line x1="16" y1="22" x2="16" y2="32" stroke="rgba(251,191,36,0.8)" strokeWidth="0.8" />
              <line x1="0"  y1="16" x2="10" y2="16" stroke="rgba(251,191,36,0.8)" strokeWidth="0.8" />
              <line x1="22" y1="16" x2="32" y2="16" stroke="rgba(251,191,36,0.8)" strokeWidth="0.8" />
            </svg>
            <div
              className="rounded-full border border-amber-400/40"
              style={{
                position: 'absolute',
                width: m.size * 0.5, height: m.size * 0.5,
                top: '50%', left: '50%',
                animation: `heroRingExpand 3s ease-out infinite`,
                animationDelay: m.delay,
                animationFillMode: 'backwards',
              }}
            />
          </div>
        ))}
      </div>

      {/* Layer 4: Technical spec annotations (desktop, right side) */}
      <div className="absolute inset-0 hidden lg:block pointer-events-none" aria-hidden="true">
        {[
          { text: 'TOL: ±0.005 mm', right: '7%',  top: '28%', delay: '0s' },
          { text: 'ALLOY: CuSn11Pb2',  right: '9%',  top: '44%', delay: '1.5s' },
          { text: 'STD: DIN 1982',    right: '6%',  top: '58%', delay: '3s' },
          { text: 'INSP: CMM / SPC', right: '5%', top: '72%', delay: '0.8s' },
        ].map((spec, i) => (
          <div
            key={i}
            className="absolute font-mono text-xs tracking-widest"
            style={{
              right: spec.right, top: spec.top,
              color: 'rgba(251,191,36,0.6)',
              animation: `heroTargetPulse 5s ease-in-out infinite`,
              animationDelay: spec.delay,
              letterSpacing: '0.12em',
            }}
          >
            {spec.text}
          </div>
        ))}
      </div>

      {/* Layer 5: Blueprint corner brackets */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <svg className="absolute top-4 left-4 opacity-25" width="40" height="40" viewBox="0 0 40 40" fill="none">
          <path d="M0 22 L0 0 L22 0" stroke="rgba(251,191,36,0.9)" strokeWidth="1.5" />
        </svg>
        <svg className="absolute top-4 right-4 opacity-25" width="40" height="40" viewBox="0 0 40 40" fill="none">
          <path d="M40 22 L40 0 L18 0" stroke="rgba(251,191,36,0.9)" strokeWidth="1.5" />
        </svg>
        <svg className="absolute bottom-4 left-4 opacity-25" width="40" height="40" viewBox="0 0 40 40" fill="none">
          <path d="M0 18 L0 40 L22 40" stroke="rgba(251,191,36,0.9)" strokeWidth="1.5" />
        </svg>
        <svg className="absolute bottom-4 right-4 opacity-25" width="40" height="40" viewBox="0 0 40 40" fill="none">
          <path d="M40 18 L40 40 L18 40" stroke="rgba(251,191,36,0.9)" strokeWidth="1.5" />
        </svg>
      </div>

      <div className="relative container mx-auto max-w-7xl px-4 sm:px-6 py-20 md:py-28">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-600/20 border border-brand-500/30 text-brand-400 text-xs font-semibold tracking-wider uppercase mb-6">
            <span className="w-1.5 h-1.5 bg-brand-500 rounded-full animate-pulse" aria-hidden="true" />
            ISO 9001 Certified · Est. 1968
          </span>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight mb-6">
            Leaders in
            <span className="text-brand-400"> Precision </span>
            Machining &amp; Casting
          </h1>

          <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8 max-w-2xl">
            We manufacture Bronze Bushings, Thrust Washers, and copper alloy components with in-house casting, CNC/VMC machining, and metrological inspection from foundry to finish.
          </p>

          <div className="flex flex-wrap gap-4">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-lg transition-colors duration-200 cursor-pointer shadow-lg shadow-brand-900/30"
            >
              Explore Products
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/infrastructure"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/15 text-white font-semibold rounded-lg border border-white/20 transition-colors duration-200 cursor-pointer"
            >
              Our Infrastructure
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* ── Stats bar ────────────────────────────────────── */}
    <section className="bg-navy-800 border-b border-white/5" aria-label="Key statistics">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
          {stats.map(({ value, label }) => (
            <div key={label} className="py-6 px-4 text-center">
              <div className="text-2xl md:text-3xl font-extrabold text-brand-400">{value}</div>
              <div className="text-xs text-slate-400 mt-1 font-medium">{label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Capabilities ─────────────────────────────────── */}
    <section className="py-20 px-4 bg-white">
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-extrabold text-navy-900 mb-3">
            End-to-End Manufacturing
          </h2>
          <p className="text-slate-500 text-lg max-w-2xl mx-auto">
            Casting to precision machining, every stage controlled under one roof.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {capabilities.map(({ title, description, link, linkLabel, color, icon }) => (
            <div
              key={title}
              className={`group bg-white border rounded-2xl p-8 transition-all duration-300 hover:shadow-xl ${capabilityBg[color]}`}
            >
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-colors duration-200 ${capabilityIconBg[color]}`}>
                {icon}
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3">{title}</h3>
              <p className="text-slate-600 leading-relaxed mb-6 text-sm">{description}</p>
              <Link
                to={link}
                className={`inline-flex items-center gap-1.5 text-sm font-semibold transition-colors duration-200 cursor-pointer ${capabilityLink[color]}`}
              >
                {linkLabel}
                <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Certifications banner ─────────────────────────── */}
    <section className="bg-slate-50 py-10 border-y border-slate-200">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Certified To</p>
          <div className="flex flex-wrap justify-center md:justify-end gap-4">
            {[
              { code: 'ISO 9001:2015', desc: 'Quality Management' },
              { code: 'ISO 45001:2018', desc: 'Health & Safety' },
              { code: 'ISO 14001:2015', desc: 'Environmental' },
            ].map(({ code, desc }) => (
              <div key={code} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-5 py-3 shadow-sm">
                <div className="w-2 h-2 bg-brand-500 rounded-full shrink-0" aria-hidden="true" />
                <div>
                  <div className="text-sm font-bold text-navy-900">{code}</div>
                  <div className="text-xs text-slate-500">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ── Products preview ─────────────────────────────── */}
    <section className="py-20 px-4 bg-slate-100">
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-extrabold text-navy-900 mb-3">Our Products</h2>
          <p className="text-slate-500 text-lg max-w-xl mx-auto">
            Bronze Bushings, Thrust Washers, and precision copper alloy components made to your drawings.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {products.map((product) => (
            <Link
              key={product.id}
              to="/products"
              className="group relative overflow-hidden rounded-2xl bg-white shadow-sm hover:shadow-xl transition-shadow duration-300 cursor-pointer"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={product.image}
                  alt={product.alt}
                  loading="lazy"
                  width="400"
                  height="300"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-3 border-t border-slate-100">
                <p className="text-xs font-semibold text-navy-700 truncate">{product.name}</p>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 px-6 py-3 bg-navy-900 hover:bg-navy-800 text-white font-semibold rounded-lg transition-colors duration-200 cursor-pointer"
          >
            View All Products
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>

    {/* ── Industries served ────────────────────────────── */}
    <section className="py-20 px-4 bg-white">
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-extrabold text-navy-900 mb-3">Industries We Serve</h2>
          <p className="text-slate-500 text-lg max-w-xl mx-auto">
            Trusted Tier-1 OEM supplier across demanding industrial sectors globally.
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
          {industries.map((ind) => (
            <span
              key={ind}
              className="px-5 py-2.5 bg-slate-50 border border-slate-200 rounded-full text-sm font-medium text-navy-700 hover:bg-brand-50 hover:border-brand-300 hover:text-brand-700 transition-colors duration-200 cursor-default"
            >
              {ind}
            </span>
          ))}
        </div>
      </div>
    </section>

    {/* ── CTA ──────────────────────────────────────────── */}
    <section className="bg-navy-900 py-16 px-4">
      <div className="container mx-auto max-w-4xl text-center">
        <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
          Ready to discuss your requirements?
        </h2>
        <p className="text-slate-400 text-lg mb-8 max-w-xl mx-auto">
          Send us your drawings or specifications and we'll respond within 24 hours.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link
            to="/contact"
            className="px-7 py-3.5 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-lg transition-colors duration-200 cursor-pointer shadow-lg shadow-brand-900/30"
          >
            Contact Us
          </Link>
          <a
            href="mailto:sales@matoindustries.com"
            className="px-7 py-3.5 bg-white/10 hover:bg-white/15 text-white font-semibold rounded-lg border border-white/20 transition-colors duration-200 cursor-pointer"
          >
            sales@matoindustries.com
          </a>
        </div>
      </div>
    </section>

    </div>
  </>
);

export default HomePage;
